import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Lightning, Envelope, Moon, PaperPlaneTilt, Sun } from "@phosphor-icons/react";
import { fmtWhen } from "./useNotificationPrefs";

const isHM = (v) => /^\d{2}:\d{2}$/.test(v || "");

function ModeTile({ active, icon: Icon, title, text, onClick, testid, children }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={active}
            className={`text-left rounded-md border p-3 transition-colors w-full ${active ? "border-primary bg-primary/5 ring-1 ring-primary/40" : "border-border hover:border-primary/40"}`}>
      <div className="flex items-center gap-2"><Icon size={16} weight="duotone" className={active ? "text-primary" : "text-muted-foreground"} /><span className="text-xs font-semibold">{title}</span></div>
      <p className="text-[11px] text-muted-foreground mt-1">{text}</p>
      {children}
    </button>
  );
}

export function EmailDeliveryCard({ data, push, sendPending }) {
  const d = data.email_delivery;
  const qh = d.quiet_hours;
  const paused = data.notify_email === false;
  const setDelivery = (patch) => push({ email_delivery: patch }, (cur) => ({ ...cur, email_delivery: { ...cur.email_delivery, ...patch, quiet_hours: { ...cur.email_delivery.quiet_hours, ...(patch.quiet_hours || {}) } } }));
  const setQuiet = (patch) => setDelivery({ quiet_hours: patch });
  const pending = data.pending?.count || 0;

  return (
    <div className={`space-y-4 ${paused ? "opacity-60" : ""}`} data-testid="email-delivery-card">
      <div className="grid sm:grid-cols-2 gap-3">
        <ModeTile active={d.mode === "instant"} icon={Envelope} title="Instant" text="One email per update, as it happens." onClick={() => setDelivery({ mode: "instant" })} testid="delivery-mode-instant" />
        <ModeTile active={d.mode === "daily"} icon={Sun} title="Daily summary" text="Everything since your last email, bundled into one message." onClick={() => setDelivery({ mode: "daily" })} testid="delivery-mode-daily">
          {d.mode === "daily" && (
            <div className="flex items-center gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
              <span className="text-[11px] text-muted-foreground">Send at</span>
              <Input type="time" step={900} value={d.digest_time} className="h-7 w-28 text-xs" data-testid="delivery-digest-time"
                     onChange={(e) => isHM(e.target.value) && setDelivery({ digest_time: e.target.value })} />
              <span className="text-[11px] text-muted-foreground">UK time</span>
            </div>
          )}
        </ModeTile>
      </div>

      <div className="rounded-md border border-border divide-y divide-border" data-testid="quiet-hours">
        <div className="flex items-center justify-between gap-3 p-3">
          <div className="flex items-start gap-2">
            <Moon size={16} weight="duotone" className="text-primary mt-0.5" />
            <div>
              <p className="text-xs font-semibold">Overnight quiet hours</p>
              <p className="text-[11px] text-muted-foreground">Hold routine emails between these times and send them together when the window ends.</p>
              {qh.enabled && (
                <div className="flex items-center gap-2 mt-2">
                  <Input type="time" step={900} value={qh.start} className="h-7 w-28 text-xs" data-testid="quiet-start" onChange={(e) => isHM(e.target.value) && setQuiet({ start: e.target.value })} />
                  <span className="text-[11px] text-muted-foreground">to</span>
                  <Input type="time" step={900} value={qh.end} className="h-7 w-28 text-xs" data-testid="quiet-end" onChange={(e) => isHM(e.target.value) && setQuiet({ end: e.target.value })} />
                </div>
              )}
            </div>
          </div>
          <Switch checked={qh.enabled} onCheckedChange={(v) => setQuiet({ enabled: v })} data-testid="quiet-enabled" />
        </div>
        <div className="flex items-center justify-between gap-3 p-3">
          <div>
            <p className="text-xs font-semibold">Hold weekends</p>
            <p className="text-[11px] text-muted-foreground">Nothing routine on Saturday or Sunday — it arrives Monday morning instead.</p>
          </div>
          <Switch checked={qh.weekends} onCheckedChange={(v) => setQuiet({ weekends: v })} data-testid="quiet-weekends" />
        </div>
      </div>

      <div className="flex items-start gap-2 text-[11px] text-muted-foreground" data-testid="delivery-urgent-note">
        <Lightning size={14} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
        <span>Emergency updates and engineer running-late alerts always email immediately. In-portal notifications are never delayed.
          {data.next_routine_email?.held && <> A routine email sent now would arrive <strong>{fmtWhen(data.next_routine_email.release_at)}</strong>.</>}</span>
      </div>

      {pending > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-sky-200 bg-sky-50 dark:bg-sky-950/20 p-3" data-testid="pending-emails">
          <p className="text-xs"><strong>{pending}</strong> email{pending === 1 ? "" : "s"} waiting · next send {fmtWhen(data.pending.next_release_at)}</p>
          <Button size="sm" variant="outline" onClick={sendPending} data-testid="send-pending-btn"><PaperPlaneTilt size={14} className="mr-1" /> Send now</Button>
        </div>
      )}
    </div>
  );
}
